import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Select from "components/shared/Select";
import ChatArea from "components/ai_tools/ChatArea";
import { useFormik } from "formik";
import * as Yup from "yup";
import pluralize from "pluralize";
import LcFetch from "lib/shared/fetch";
import { AccessTierProvider } from "contexts/AccessTierContext";
import AIToolsLayout from "./layout/AIToolsLayout";
import {
  AI_TOOL_CHANNELS,
  AI_TOOL_NAMES,
  AI_TOOLS_LABEL_MAP,
  AI_TOOLS_LOADING_DEFAULTS,
  REUSABLES
} from "./constants";
import useChatMessages from "./hooks/useChatMessages";
import AIFormLayout from "./shared/AIForm/AIFormLayout";
import useFormDisableFields from "./hooks/useFormDisableFields";
import useWindowResizeAndFormCollapse from "./hooks/useWindowResizeAndFormCollapse";

const ContextualizedQuizQuestions = (props) => {
  return (
    <AccessTierProvider accessTier={props.accessTier}>
      <QuizQuestions {...props} />
    </AccessTierProvider>
  );
};

const QuizQuestions = (props) => {
  const {
    aiAnswersCompletePath,
    infiniteAnswers,
    locked,
    options,
    quotaRemaining: quotaFromProps,
    signupPath,
    submissionPath,
    upgradeable
  } = props;

  const validationSchema = Yup.object().shape({
    subtopic: Yup.string().when("topic", (_) => {
      if (selectedTopic?.data) {
        return Yup.string().required(
          `Please select your ${
            AI_TOOLS_LABEL_MAP[selectedTopic.label] ??
            selectedTopic.labelText?.toLowerCase()
          }`
        );
      }
    }),
    title: Yup.string().required("Please select your title"),
    topic: Yup.string().when("title", (title) => {
      if (title) {
        return Yup.string().required("Please select your topic");
      }
    })
  });

  const [loadingTopics, setLoadingTopics] = useState(false);
  const [topics, setTopics] = useState();
  const [selectedTopic, setSelectedTopic] = useState();
  const [selectedSubtopic, setSelectedSubtopic] = useState();
  const { isLessThanSmallBreakpoint, collapsed, setCollapsed } =
    useWindowResizeAndFormCollapse();

  const formik = useFormik({
    initialValues: {
      subtopic: "",
      title: prevSlug || "",
      topic: ""
    },
    onSubmit: (values) => {
      validationSchema
        .validate(values, { abortEarly: false })
        .then(() => {
          handleQuizDiscussionEssayPrompt(values);
          if (isLessThanSmallBreakpoint) {
            setCollapsed(true);
          }
        })
        .catch((errors) => {
          formik.setErrors(errors);
        });
    },
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema
  });

  const {
    handleQuizDiscussionEssayPrompt,
    isStreaming,
    loadingPastMessages,
    quotaRemaining,
    messages,
    prevSlug
  } = useChatMessages({
    aiAnswersCompletePath,
    channel: AI_TOOL_CHANNELS.QUIZ_QUESTIONS,
    eventCategory: "Quiz Questions Generator",
    formik,
    infiniteAnswers,
    locked,
    messageText: selectedSubtopic?.messageText ?? selectedTopic?.messageText,
    quotaFromProps,
    signupPath,
    submissionPath,
    toolName: AI_TOOL_NAMES.QUIZ_QUESTIONS,
    upgradeable
  });

  const { disableFields } = useFormDisableFields(
    locked,
    quotaRemaining,
    infiniteAnswers
  );

  const handleError = (timeoutRef) => {
    clearTimeout(timeoutRef);
    setLoadingTopics(false);
    setTopics([]);
    formik.setFieldError("topic", "Data request failed. Please try again.");
  };

  const fetchDropdownContent = (guidePath) => {
    setTopics();
    setSelectedTopic(null);
    setSelectedSubtopic(null);
    const loadingTimeout = setTimeout(() => {
      setTopics([]);
      setLoadingTopics(true);
    }, 1000);

    LcFetch(guidePath, {
      method: "GET"
    })
      .then((response) => {
        if (!response.ok) {
          handleError(loadingTimeout);
        } else {
          return response.json();
        }
      })
      .then((data) => {
        clearTimeout(loadingTimeout);
        setTopics(data.topics);
        setLoadingTopics(false);
      })
      .catch((error) => {
        handleError(loadingTimeout);
        console.error("Error fetching dropdown content:", error);
      });
  };

  useEffect(() => {
    formik.setFieldValue("title", prevSlug);
  }, [prevSlug]);

  return (
    <AIToolsLayout dataTestId="QuizQuestions">
      <AIFormLayout
        {...props}
        collapsed={collapsed}
        formik={formik}
        isLessThanSmallBreakpoint={isLessThanSmallBreakpoint}
        loading={isStreaming || loadingPastMessages}
        quotaRemaining={quotaRemaining}
        setCollapsed={setCollapsed}
        storageKey="quizQuestions"
        toolName={AI_TOOL_NAMES.QUIZ_QUESTIONS}
      >
        <Select
          errorMessage={formik.errors.title}
          id={REUSABLES.TITLE}
          isDisabled={isStreaming || loadingPastMessages || disableFields}
          isSearchable
          labelText="Title"
          name={REUSABLES.TITLE}
          options={options}
          onChange={(e) => {
            formik.setFieldValue(REUSABLES.TITLE, e.value);
            formik.setFieldValue(REUSABLES.TOPIC, undefined);
            formik.setFieldValue(REUSABLES.SUBTOPIC, undefined);
            formik.setFieldError(REUSABLES.TOPIC, undefined);
            formik.setFieldError(REUSABLES.SUBTOPIC, undefined);
            fetchDropdownContent(e.guide_path);
          }}
          placeholder={!disableFields ? "e.g. Hamlet (Shakespeare)" : ""}
          value={formik.values.title}
        />
        {topics && !disableFields && (
          <Select
            errorMessage={
              loadingTopics && formik.errors.topic
                ? "Loading, select one when complete"
                : formik.errors.topic
            }
            id={REUSABLES.TOPIC}
            isControlled
            isDisabled={
              loadingTopics ||
              isStreaming ||
              loadingPastMessages ||
              disableFields
            }
            isSearchable
            isLoading={loadingTopics}
            labelText="Topic"
            name={REUSABLES.TOPIC}
            options={loadingTopics ? [AI_TOOLS_LOADING_DEFAULTS] : topics}
            onChange={(e) => {
              formik.setFieldValue(REUSABLES.TOPIC, e.value);
              formik.setFieldValue(REUSABLES.SUBTOPIC, undefined);
              formik.setFieldError(REUSABLES.SUBTOPIC, undefined);

              setSelectedTopic(topics.find((topic) => topic.value === e.value));
              setSelectedSubtopic(null);
            }}
            placeholder={!disableFields ? "e.g. Themes" : ""}
            value={
              loadingTopics
                ? AI_TOOLS_LOADING_DEFAULTS.value
                : formik.values.topic
            }
          />
        )}
        {selectedTopic?.data && !disableFields && (
          <Select
            errorMessage={formik.errors.subtopic}
            id={REUSABLES.SUBTOPIC}
            isControlled
            isDisabled={isStreaming || loadingPastMessages || disableFields}
            isSearchable
            labelText={
              selectedTopic.labelText ??
              pluralize(formik.values.topic, 1, false)
            }
            name={REUSABLES.SUBTOPIC}
            options={selectedTopic.data}
            onChange={(e) => {
              formik.setFieldValue(REUSABLES.SUBTOPIC, e.value);
              setSelectedSubtopic(
                selectedTopic.data.find((topic) => topic.value === e.value)
              );
            }}
            placeholder={!disableFields ? "Select one" : ""}
            value={formik.values.subtopic}
          />
        )}
      </AIFormLayout>
      <ChatArea messages={messages} />
    </AIToolsLayout>
  );
};

QuizQuestions.propTypes = {
  aiAnswersCompletePath: PropTypes.string,
  locked: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.string
    })
  ),
  questionMinimum: PropTypes.number,
  quotaRemaining: PropTypes.number.isRequired,
  resetDate: PropTypes.string,
  signupPath: PropTypes.string,
  submissionPath: PropTypes.string,
  upgradeable: PropTypes.bool
};

QuizQuestions.defaultProps = {
  aiAnswersCompletePath: "/ai_answers/complete",
  locked: false,
  options: [],
  questionMinimum: 25,
  resetDate: "",
  signupPath: "/sign-up?focus=teacher",
  submissionPath: "/ai/quiz-question-generator",
  upgradeable: false
};

export default ContextualizedQuizQuestions;
